// Matemática de tiles (Web Mercator / EPSG:3857) e cálculo dos tiles de satélite
// que cobrem uma fazenda — usado p/ baixar tiles na sync e ter o mapa interativo
// offline (Leaflet lendo do cache).

type LngLat = [number, number];
interface BBox { minLon: number; maxLon: number; minLat: number; maxLat: number }

export function lon2tile(lon: number, z: number): number {
  return ((lon + 180) / 360) * 2 ** z;
}
export function lat2tile(lat: number, z: number): number {
  const r = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z;
}

export const ESRI_TILE = (z: number, x: number, y: number): string =>
  `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

// Satélite do Google (mesmo do mapa online). Permite fetch com CORS → dá pra
// baixar e cachear pro offline. Subdomínios mt0..3 balanceiam a carga.
export const GOOGLE_SAT_TILE = (z: number, x: number, y: number): string =>
  `https://mt${(x + y) % 4}.google.com/vt/lyrs=s&x=${x}&y=${y}&z=${z}`;

// Só os rótulos (nomes de município/estrada), fundo transparente — vai por cima
// do satélite no zoom-out pra o agrônomo se situar. Precisa de rede.
export const GOOGLE_LABELS_TEMPLATE = 'https://{s}.google.com/vt/lyrs=h&x={x}&y={y}&z={z}';

function eachRing(gj: unknown, fn: (ring: LngLat[]) => void): void {
  const g = gj as { type?: string; coordinates?: unknown; geometry?: unknown; features?: unknown[] } | null;
  if (!g || typeof g !== 'object') return;
  if (g.type === 'Polygon') (g.coordinates as LngLat[][]).forEach((r) => fn(r));
  else if (g.type === 'MultiPolygon') (g.coordinates as LngLat[][][]).forEach((p) => p.forEach((r) => fn(r)));
  else if (g.type === 'Feature') eachRing(g.geometry, fn);
  else if (g.type === 'FeatureCollection') (g.features ?? []).forEach((f) => eachRing(f, fn));
}

export function bboxOf(geojsons: unknown[]): BBox | null {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity, any = false;
  for (const gj of geojsons) {
    eachRing(gj, (ring) => {
      for (const [lon, lat] of ring) {
        any = true;
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
    });
  }
  if (!any) return null;
  const dx = (maxLon - minLon) * 0.1 || 0.002;
  const dy = (maxLat - minLat) * 0.1 || 0.002;
  return { minLon: minLon - dx, maxLon: maxLon + dx, minLat: minLat - dy, maxLat: maxLat + dy };
}

export const OFFLINE_MIN_ZOOM = 12;

type Tile = { z: number; x: number; y: number };

function tilesAtZoom(bb: NonNullable<ReturnType<typeof bboxOf>>, z: number, margin: number): Tile[] {
  const x0 = Math.floor(lon2tile(bb.minLon, z)) - margin;
  const x1 = Math.floor(lon2tile(bb.maxLon, z)) + margin;
  const y0 = Math.floor(lat2tile(bb.maxLat, z)) - margin;
  const y1 = Math.floor(lat2tile(bb.minLat, z)) + margin;
  const out: Tile[] = [];
  for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) if (x >= 0 && y >= 0) out.push({ z, x, y });
  return out;
}

// Zoom a partir do qual o cache passa a cobrir SÓ os talhões (não a fazenda toda).
// Abaixo dele: overview da fazenda inteira. Acima: só sobre os polígonos — assim dá
// pra ir fundo (z19, qualidade real) sem estourar o nº de tiles nos vãos entre talhões.
const DETAIL_ZOOM = 16;

// Tiles que cobrem a fazenda de zMin a zMax, adicionando um zoom SÓ se ele couber
// INTEIRO no orçamento (nunca meio zoom → nunca "metade em branco"). Zoom baixo cobre
// o bbox da fazenda; zoom alto cobre cada talhão (economia p/ zoom fundo com qualidade).
// Devolve o zoom máximo completo, pra o mapa saber até onde tem tile nativo.
export function tilesForGeojsons(
  geojsons: unknown[],
  zMin = 12,
  zMax = 20,
  budget = 2200,
): { tiles: Tile[]; maxZoom: number } {
  const fazBB = bboxOf(geojsons);
  if (!fazBB) return { tiles: [], maxZoom: zMin };
  const talhaoBBs = geojsons.map((g) => bboxOf([g])).filter((b): b is BBox => b != null);
  const tiles: Tile[] = [];
  let maxZoom = zMin;
  for (let z = zMin; z <= zMax; z++) {
    const src = z < DETAIL_ZOOM ? [fazBB] : talhaoBBs.length ? talhaoBBs : [fazBB];
    const margin = z >= 19 ? 0 : 1; // no zoom mais fundo, só o talhão (sem borda) — economia
    const zseen = new Set<string>();
    const zt: Tile[] = [];
    for (const bb of src) {
      for (const t of tilesAtZoom(bb, z, margin)) {
        const k = `${t.x}/${t.y}`;
        if (!zseen.has(k)) { zseen.add(k); zt.push(t); }
      }
    }
    if (z > zMin && tiles.length + zt.length > budget) break; // não adiciona zoom parcial
    tiles.push(...zt);
    maxZoom = z;
  }
  return { tiles, maxZoom };
}

// Centro (lon/lat) do bbox dos talhões — usado pra registrar o mosaico Sentinel.
export function centerOf(geojsons: unknown[]): { lon: number; lat: number } | null {
  const bb = bboxOf(geojsons);
  if (!bb) return null;
  return { lon: (bb.minLon + bb.maxLon) / 2, lat: (bb.minLat + bb.maxLat) / 2 };
}

// ── Sentinel-2 (Microsoft Planetary Computer) — API pública, sem chave ──────
// Registra um mosaico da cena mais recente (≤80 dias, menos nuvem) sobre o ponto.
export async function registerSentinelMosaic(lon: number, lat: number): Promise<string> {
  const iso = (ms: number) => new Date(ms).toISOString().slice(0, 19) + 'Z';
  const now = Date.now();
  const body = {
    'filter-lang': 'cql2-json',
    filter: { op: 'and', args: [
      { op: '=', args: [{ property: 'collection' }, 'sentinel-2-l2a'] },
      { op: '<=', args: [{ property: 'eo:cloud_cover' }, 30] },
      { op: 's_intersects', args: [{ property: 'geometry' }, { type: 'Point', coordinates: [lon, lat] }] },
      { op: 'anyinteracts', args: [{ property: 'datetime' }, { interval: [iso(now - 80 * 86400000), iso(now)] }] },
    ] },
    sortby: [{ field: 'eo:cloud_cover', direction: 'asc' }],
  };
  try {
    const r = await fetch('https://planetarycomputer.microsoft.com/api/data/v1/mosaic/register', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (!r.ok) return '';
    return ((await r.json()) as { id?: string }).id ?? '';
  } catch {
    return '';
  }
}
function s2Template(sid: string, assets: string[], expr: string, rescale: string, colormap: string): string {
  const a = assets.map((x) => 'assets=' + x).join('&');
  return `https://planetarycomputer.microsoft.com/api/data/v1/mosaic/${sid}/tiles/WebMercatorQuad/{z}/{x}/{y}`
    + `?collection=sentinel-2-l2a&asset_as_band=true&${a}&expression=${encodeURIComponent(expr)}`
    + `&rescale=${encodeURIComponent(rescale)}&colormap_name=${colormap}&nodata=0`;
}
export const s2NdviTemplate = (sid: string) => s2Template(sid, ['B04', 'B08'], '(B08-B04)/(B08+B04)', '0,0.8', 'rdylgn');
export const s2NdmiTemplate = (sid: string) => s2Template(sid, ['B08', 'B11'], '(B08-B11)/(B08+B11)', '-0.2,0.4', 'rdylbu');
// SAVI (vigor com ajuste de solo, L=0.5), GNDVI (clorofila/N), NDRE (red edge),
// NDWI (água/lâmina, McFeeters) — mesmos bandos Sentinel-2, só muda a expressão.
export const s2SaviTemplate = (sid: string) => s2Template(sid, ['B04', 'B08'], '1.5*(B08-B04)/(B08+B04+0.5)', '0,0.8', 'rdylgn');
export const s2GndviTemplate = (sid: string) => s2Template(sid, ['B03', 'B08'], '(B08-B03)/(B08+B03)', '0,0.8', 'rdylgn');
export const s2NdreTemplate = (sid: string) => s2Template(sid, ['B05', 'B08'], '(B08-B05)/(B08+B05)', '0,0.5', 'rdylgn');
export const s2NdwiTemplate = (sid: string) => s2Template(sid, ['B03', 'B08'], '(B03-B08)/(B03+B08)', '-0.3,0.5', 'rdylbu');
export const tileFromTemplate = (tpl: string, z: number, x: number, y: number) =>
  tpl.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
